import { useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import { Card, CardActionArea, CardContent, Theme, Typography, useTheme } from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/Business';
import WorkIcon from '@material-ui/icons/Work';
import { Skeleton } from '@material-ui/lab';
import * as React from 'react';

import {
    AgentDraftClientUpdate,
    AgentDraftJobUpdate,
    GET_AGENT_DRAFT_CLIENT_UPDATE,
    GET_AGENT_DRAFT_JOB_UPDATE
} from '../../graphql/queries/agent';
import { useAgent } from '../../hooks/use-agent';
import { UpdateDiffDialog } from './update-diff-dialog';
import { checkStaleness, updateDraftBadgeStyles } from './update-diff-utils';

interface UpdateDraftFeedProps {
    draftId: string;
    draftType: 'client' | 'job';
}

const SKELETON_HEIGHT = 60;

const styles = (theme: Theme) => css`
    ${updateDraftBadgeStyles(theme)}

    margin: 8px 0;
    background: #fcfcfa;

    .content {
        padding: 12px 16px !important;
    }

    .titleRow {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .titleLeft {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .icon {
        font-size: 20px;
        color: ${theme.palette.primary.main};
        flex-shrink: 0;
    }

    .title {
        font-weight: 600;
        line-height: 1.3;
    }

    .explanation {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        font-size: 14px;
        line-height: 20px;
        margin-top: 12px;
    }

    /* Feed-specific badge overrides for smaller size */
    .badge {
        font-size: 10px;
        padding: 2px 6px;
        border-radius: 10px;
        flex-shrink: 0;
    }
`;

export const UpdateDraftFeed: React.FC<UpdateDraftFeedProps> = ({ draftId, draftType }) => {
    const theme = useTheme();
    const rootStyles = styles(theme);
    const agent = useAgent();
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [sidebarWasOpen, setSidebarWasOpen] = React.useState(false);

    const { data: clientData, loading: clientLoading } = useQuery<{ draft: AgentDraftClientUpdate | null }>(
        GET_AGENT_DRAFT_CLIENT_UPDATE,
        {
            skip: draftType !== 'client',
            variables: { id: draftId }
        }
    );

    const { data: jobData, loading: jobLoading } = useQuery<{ draft: AgentDraftJobUpdate | null }>(
        GET_AGENT_DRAFT_JOB_UPDATE,
        {
            skip: draftType !== 'job',
            variables: { id: draftId }
        }
    );

    const loading = clientLoading || jobLoading;
    const draft = draftType === 'client' ? clientData?.draft : jobData?.draft;

    const openDialog = React.useCallback(() => {
        const wasOpen = agent?.open ?? false;
        setSidebarWasOpen(wasOpen);
        agent?.onClose();
        setDialogOpen(true);
    }, [agent]);

    const closeDialog = React.useCallback(() => {
        setDialogOpen(false);
        if (sidebarWasOpen) {
            agent?.onOpen();
        }
    }, [agent, sidebarWasOpen]);

    if (loading || !draft) {
        return (
            <Card variant="outlined" css={rootStyles}>
                <CardContent className="content">
                    <Skeleton variant="rect" width="100%" height={SKELETON_HEIGHT} />
                </CardContent>
            </Card>
        );
    }

    let displayTitle: string;
    let titlePrefix: string;
    let Icon: typeof WorkIcon | typeof BusinessIcon;

    if (draftType === 'job') {
        const jobDraft = draft as AgentDraftJobUpdate;
        const jobTitle = jobDraft.job?.title || 'Unknown Job';
        const clientName = jobDraft.job?.client?.name || '';
        displayTitle = clientName ? `${jobTitle} @ ${clientName}` : jobTitle;
        titlePrefix = 'Job Update';
        Icon = WorkIcon;
    } else {
        const clientDraft = draft as AgentDraftClientUpdate;
        displayTitle = clientDraft.client?.name || 'Unknown Client';
        titlePrefix = 'Client Update';
        Icon = BusinessIcon;
    }

    // Detect staleness
    const isResolved = Boolean(draft.appliedAt || draft.dismissedAt);
    let isStale = false;
    if (!isResolved) {
        if (draftType === 'client') {
            const d = draft as AgentDraftClientUpdate;
            const currentClient = d.client as Record<string, unknown> | null;
            isStale = checkStaleness(d.updates.old || {}, currentClient);
        } else {
            const d = draft as AgentDraftJobUpdate;
            const currentJob = d.job as Record<string, unknown> | null;
            const currentJobDesc = d.job?.description as Record<string, unknown> | null;
            if (d.updates.job) {
                isStale = checkStaleness(d.updates.job.old, currentJob);
            }
            if (!isStale && d.updates.jobDescription) {
                isStale = checkStaleness(d.updates.jobDescription.old, currentJobDesc);
            }
        }
    }

    const statusBadge = draft.appliedAt ? (
        <span className="badge badge--applied">Applied</span>
    ) : draft.dismissedAt ? (
        <span className="badge badge--dismissed">Dismissed</span>
    ) : isStale ? (
        <span className="badge badge--stale">Stale</span>
    ) : null;

    const explanationPreview = draft.explanation ? (
        <Typography variant="body2" color="textSecondary" className="explanation">
            {draft.explanation}
        </Typography>
    ) : null;

    return (
        <>
            <Card variant="outlined" css={rootStyles}>
                <CardActionArea onClick={openDialog}>
                    <CardContent className="content">
                        <div className="titleRow">
                            <div className="titleLeft">
                                <Icon className="icon" />
                                <Typography variant="subtitle1" className="title">
                                    {titlePrefix}: {displayTitle}
                                </Typography>
                            </div>
                            {statusBadge}
                        </div>
                        {explanationPreview}
                    </CardContent>
                </CardActionArea>
            </Card>

            <UpdateDiffDialog open={dialogOpen} onClose={closeDialog} draftId={draftId} draftType={draftType} />
        </>
    );
};
